import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listRecentSales } from "../api/atomicApi";
import { IpfsImage } from "../components/IpfsImage";
import { assetImageHash, formatAssetName } from "../utils/backing";
import { formatRelativeTime } from "../utils/time";
import type { MarketSale } from "../types";

const REFRESH_MS = 30000; // re-fetch the list itself every 30s to pick up new sales
const TICK_MS = 5000; // re-render "X ago" labels every 5s without a full re-fetch

function saleTimeMs(sale: MarketSale): number {
  const raw = sale.updated_at_time;
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) ? n : 0;
}

/**
 * "Recently Sold" — the last 100 completed sales across all of AtomicMarket
 * (not just this site's own listings), newest first, with a live-updating
 * relative time per row ("seconds/minutes/hours ago") — the same feed
 * AtomicHub shows near the bottom of its own marketplace, pulled out into
 * its own dedicated tab here. See listRecentSales in atomicApi.ts for the
 * state=3 (SOLD) / sort=updated query this is built on.
 */
export function RecentlySold() {
  const [sales, setSales] = useState<MarketSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0); // forces a re-render so relative times stay fresh

  useEffect(() => {
    let cancelled = false;

    function load() {
      listRecentSales(100)
        .then((data) => {
          if (!cancelled) {
            setSales(data);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load recent sales");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    load();
    const refreshId = window.setInterval(load, REFRESH_MS);
    const tickId = window.setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(refreshId);
      window.clearInterval(tickId);
    };
  }, []);

  return (
    <div className="page">
      <div className="page__header">
        <h1>Recently Sold</h1>
        <p className="page__subtitle">
          The last 100 completed sales on WAX, across every collection — refreshes automatically every 30 seconds.
        </p>
      </div>

      {loading && <div className="state-message">Loading recent sales…</div>}
      {error && (
        <div className="state-message state-message--error">Couldn't load recent sales from the AtomicHub API: {error}</div>
      )}
      {!loading && !error && sales.length === 0 && <div className="state-message">No completed sales found.</div>}

      {sales.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th></th>
                <th>Item</th>
                <th>Collection</th>
                <th>Price</th>
                <th>Seller</th>
                <th>Buyer</th>
                <th>Sold</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => {
                const asset = sale.assets[0];
                const price = `${(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(
                  sale.price.token_precision
                )} ${sale.price.token_symbol}`;
                return (
                  <tr key={sale.sale_id}>
                    <td className="recently-sold__thumb">
                      {asset && (
                        <Link to={`/asset/${asset.asset_id}`}>
                          <IpfsImage
                            hash={assetImageHash(asset)}
                            alt={formatAssetName(asset)}
                            className="recently-sold__img"
                            placeholderClassName="recently-sold__img-placeholder"
                            loading="lazy"
                          />
                        </Link>
                      )}
                    </td>
                    <td>
                      {asset ? (
                        <Link to={`/asset/${asset.asset_id}`}>{formatAssetName(asset)}</Link>
                      ) : (
                        `Sale #${sale.sale_id}`
                      )}
                    </td>
                    <td>{sale.collection_name}</td>
                    <td>{price}</td>
                    <td>{sale.seller}</td>
                    <td>{sale.buyer ?? "—"}</td>
                    <td>{formatRelativeTime(saleTimeMs(sale))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
